<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, reactive, computed, onMounted } from 'vue';
import leadFormsAPI from 'dashboard/api/nexceo/leadForms';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'contact', label: 'Contact Form' },
  { key: 'popup', label: 'Popup Form' },
  { key: 'embed', label: 'Embed Code' },
  { key: 'submissions', label: 'Submissions' },
  { key: 'settings', label: 'Settings' },
];

const activeTab = ref('overview');
const isLoading = ref(false);
const loadError = ref(null);
const isSavingContact = ref(false);
const isSavingPopup = ref(false);
const isSavingSettings = ref(false);
const contactSaved = ref(false);
const popupSaved = ref(false);
const settingsSaved = ref(false);
const isLoadingSubs = ref(false);
const showContactPreview = ref(false);
const showPopupPreview = ref(false);

const stats = reactive({});
const projects = ref([]);
const forms = ref([]);
const allSubmissions = ref([]);
const allowedDomainsInput = ref('');

const subFilter = reactive({ form_type: '', status: '' });

const popupTitleOptions = [
  'Get Project Details on WhatsApp',
  'Get Price & Brochure',
  'Book a Free Site Visit',
  'Talk to Property Expert',
];

const optionalContactFields = [
  { key: 'show_email', label: 'Email' },
  { key: 'show_bhk', label: 'BHK / Requirement' },
  { key: 'show_budget', label: 'Budget Range' },
  { key: 'show_location', label: 'Preferred Location' },
  { key: 'show_purpose', label: 'Purpose' },
  { key: 'show_timeline', label: 'Timeline' },
  { key: 'show_message', label: 'Message' },
];

const workflowSteps = [
  'Website Visitor',
  'Form Submit',
  'NEXCEO Lead',
  'ZOYA Qualification',
  'Project AI',
  'Human Agent',
  'Kanban / Calendar / Reports',
];

const defaultContactDraft = () => ({
  id: null,
  enabled: false,
  title: '',
  subtitle: '',
  button_text: '',
  thank_you_message: '',
  consent_text: '',
  default_project_id: '',
  allow_project_selection: true,
  show_email: true,
  show_bhk: true,
  show_budget: true,
  show_location: true,
  show_purpose: true,
  show_timeline: true,
  show_message: true,
  redirect_url: '',
  allowed_domains: [],
  settings: {},
  trigger_config: {},
});

const defaultPopupDraft = () => ({
  id: null,
  enabled: false,
  title: '',
  subtitle: '',
  button_text: '',
  thank_you_message: '',
  consent_text: '',
  default_project_id: '',
  allow_project_selection: true,
  allowed_domains: [],
  settings: {},
  trigger_config: {
    show_after_seconds: 5,
    show_again_after_days: 7,
    exit_intent: false,
    hide_on_mobile: false,
  },
});

const contactDraft = reactive(defaultContactDraft());
const popupDraft = reactive(defaultPopupDraft());

const contactForm = computed(() =>
  forms.value.find(f => f.form_type === 'contact')
);
const popupForm = computed(() =>
  forms.value.find(f => f.form_type === 'popup')
);

const isDirtyContact = computed(
  () => contactDraft.enabled !== (contactForm.value?.enabled ?? false)
);
const isDirtyPopup = computed(
  () => popupDraft.enabled !== (popupForm.value?.enabled ?? false)
);

const host = window.location.origin;

const contactFormUrl = computed(() =>
  contactForm.value
    ? `${host}/nexceo/forms/contact/${contactForm.value.public_token}`
    : ''
);

const popupFormUrl = computed(() =>
  popupForm.value
    ? `${host}/nexceo/forms/popup/${popupForm.value.public_token}`
    : ''
);

const contactIframeCode = computed(() => {
  if (!contactForm.value) return '';
  return `<iframe\n  src="${contactFormUrl.value}"\n  width="100%"\n  height="720"\n  style="border:0;border-radius:12px;"\n></iframe>`;
});

const popupScriptCode = computed(() => {
  if (!popupForm.value) return '';
  return `<script\n  src="${host}/nexceo/forms/widget.js"\n  data-token="${popupForm.value.public_token}"\n  data-form-type="popup"\n>${'<'}/script>`;
});

const allAllowedDomains = computed(() => {
  const all = [
    ...(contactForm.value?.allowed_domains || []),
    ...(popupForm.value?.allowed_domains || []),
  ];
  return [...new Set(all)];
});

const setupChecklist = computed(() => [
  { label: 'Contact form enabled', done: !!contactForm.value?.enabled },
  { label: 'Popup enabled', done: !!popupForm.value?.enabled },
  {
    label: 'Allowed domain configured',
    done: allAllowedDomains.value.length > 0,
  },
  { label: 'Project options available', done: projects.value.length > 0 },
  { label: 'AI enabled', done: true },
]);

async function loadData() {
  isLoading.value = true;
  loadError.value = null;
  try {
    const formsRes = await leadFormsAPI.list();
    forms.value = Array.isArray(formsRes.data) ? formsRes.data : [];

    // Non-critical: stats and projects failures don't block the page
    try {
      const statsRes = await leadFormsAPI.globalStats();
      Object.assign(stats, statsRes.data || {});
    } catch {
      /* stats unavailable — page still usable */
    }
    try {
      const projectsRes = await nexceoProjectOptionsAPI.list();
      projects.value = Array.isArray(projectsRes.data) ? projectsRes.data : [];
    } catch {
      /* projects unavailable — page still usable */
    }

    const cf = contactForm.value;
    if (cf) {
      Object.assign(contactDraft, {
        id: cf.id,
        enabled: cf.enabled,
        title: cf.title || '',
        subtitle: cf.subtitle || '',
        button_text: cf.button_text || '',
        thank_you_message: cf.thank_you_message || '',
        consent_text: cf.consent_text || '',
        default_project_id: cf.default_project_id || '',
        allow_project_selection: cf.allow_project_selection,
        show_email: cf.show_email,
        show_bhk: cf.show_bhk,
        show_budget: cf.show_budget,
        show_location: cf.show_location,
        show_purpose: cf.show_purpose,
        show_timeline: cf.show_timeline,
        show_message: cf.show_message,
        redirect_url: cf.redirect_url || '',
        allowed_domains: cf.allowed_domains || [],
        settings: cf.settings || {},
        trigger_config: cf.trigger_config || {},
      });
    }

    const pf = popupForm.value;
    if (pf) {
      Object.assign(popupDraft, {
        id: pf.id,
        enabled: pf.enabled,
        title: pf.title || '',
        subtitle: pf.subtitle || '',
        button_text: pf.button_text || '',
        thank_you_message: pf.thank_you_message || '',
        consent_text: pf.consent_text || '',
        default_project_id: pf.default_project_id || '',
        allow_project_selection: pf.allow_project_selection,
        allowed_domains: pf.allowed_domains || [],
        settings: pf.settings || {},
        trigger_config: pf.trigger_config || {
          show_after_seconds: 5,
          show_again_after_days: 7,
          exit_intent: false,
          hide_on_mobile: false,
        },
      });
    }

    allowedDomainsInput.value = allAllowedDomains.value.join(', ');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      '[NEXCEO] Lead Generation loadData failed:',
      e?.response?.status,
      e?.message
    );
    loadError.value = `Failed to load lead generation data. (${e?.response?.status || e?.message || 'network error'})`;
  } finally {
    isLoading.value = false;
  }
}

function buildContactPayload() {
  return {
    enabled: contactDraft.enabled,
    title: contactDraft.title,
    subtitle: contactDraft.subtitle,
    button_text: contactDraft.button_text,
    thank_you_message: contactDraft.thank_you_message,
    consent_text: contactDraft.consent_text,
    default_project_id: contactDraft.default_project_id || null,
    allow_project_selection: contactDraft.allow_project_selection,
    show_email: contactDraft.show_email,
    show_bhk: contactDraft.show_bhk,
    show_budget: contactDraft.show_budget,
    show_location: contactDraft.show_location,
    show_purpose: contactDraft.show_purpose,
    show_timeline: contactDraft.show_timeline,
    show_message: contactDraft.show_message,
    redirect_url: contactDraft.redirect_url,
    allowed_domains: allowedDomainsInput.value
      .split(',')
      .map(d => d.trim())
      .filter(Boolean),
    settings: contactDraft.settings,
    trigger_config: contactDraft.trigger_config,
  };
}

function buildPopupPayload() {
  return {
    enabled: popupDraft.enabled,
    title: popupDraft.title,
    subtitle: popupDraft.subtitle,
    button_text: popupDraft.button_text,
    thank_you_message: popupDraft.thank_you_message,
    consent_text: popupDraft.consent_text,
    default_project_id: popupDraft.default_project_id || null,
    allow_project_selection: popupDraft.allow_project_selection,
    allowed_domains: allowedDomainsInput.value
      .split(',')
      .map(d => d.trim())
      .filter(Boolean),
    settings: popupDraft.settings,
    trigger_config: popupDraft.trigger_config,
  };
}

async function saveContactForm() {
  isSavingContact.value = true;
  contactSaved.value = false;
  try {
    const payload = buildContactPayload();
    if (contactDraft.id) {
      const res = await leadFormsAPI.update(contactDraft.id, payload);
      const idx = forms.value.findIndex(f => f.id === contactDraft.id);
      if (idx !== -1) forms.value[idx] = res.data;
    } else {
      const res = await leadFormsAPI.create({
        ...payload,
        form_type: 'contact',
        name: 'Contact Form',
      });
      forms.value.push(res.data);
      contactDraft.id = res.data.id;
    }
    contactSaved.value = true;
    setTimeout(() => {
      contactSaved.value = false;
    }, 3000);
  } catch (e) {
    alert('Failed to save contact form. Please try again.');
  } finally {
    isSavingContact.value = false;
  }
}

async function savePopupForm() {
  isSavingPopup.value = true;
  popupSaved.value = false;
  try {
    const payload = buildPopupPayload();
    if (popupDraft.id) {
      const res = await leadFormsAPI.update(popupDraft.id, payload);
      const idx = forms.value.findIndex(f => f.id === popupDraft.id);
      if (idx !== -1) forms.value[idx] = res.data;
    } else {
      const res = await leadFormsAPI.create({
        ...payload,
        form_type: 'popup',
        name: 'Popup Form',
      });
      forms.value.push(res.data);
      popupDraft.id = res.data.id;
    }
    popupSaved.value = true;
    setTimeout(() => {
      popupSaved.value = false;
    }, 3000);
  } catch (e) {
    alert('Failed to save popup form. Please try again.');
  } finally {
    isSavingPopup.value = false;
  }
}

async function saveSettings() {
  isSavingSettings.value = true;
  settingsSaved.value = false;
  try {
    const domains = allowedDomainsInput.value
      .split(',')
      .map(d => d.trim())
      .filter(Boolean);

    const saves = [];
    if (contactDraft.id) {
      saves.push(
        leadFormsAPI.update(contactDraft.id, {
          enabled: contactDraft.enabled,
          allowed_domains: domains,
        })
      );
    }
    if (popupDraft.id) {
      saves.push(
        leadFormsAPI.update(popupDraft.id, {
          enabled: popupDraft.enabled,
          allowed_domains: domains,
        })
      );
    }
    await Promise.all(saves);
    await loadData();
    settingsSaved.value = true;
    setTimeout(() => {
      settingsSaved.value = false;
    }, 3000);
  } catch (e) {
    alert('Failed to save settings. Please try again.');
  } finally {
    isSavingSettings.value = false;
  }
}

async function loadSubmissions() {
  isLoadingSubs.value = true;
  allSubmissions.value = [];
  try {
    const formList = forms.value;
    const results = await Promise.all(
      formList.map(f => leadFormsAPI.submissions(f.id, subFilter))
    );
    const combined = results.flatMap(r => r.data?.submissions || []);
    combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    allSubmissions.value = combined;
  } catch (e) {
    allSubmissions.value = [];
  } finally {
    isLoadingSubs.value = false;
  }
}

function copyCode(text) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => {
      alert('Copied to clipboard!');
    })
    .catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert('Copied!');
    });
}

function statusBadgeClass(status) {
  return (
    {
      converted: 'badge-green',
      duplicate: 'badge-yellow',
      rejected: 'badge-red',
      spam: 'badge-red',
      received: 'badge-blue',
    }[status] || 'badge-gray'
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

onMounted(async () => {
  await loadData();
  if (activeTab.value === 'submissions') await loadSubmissions();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <div class="flex-1 overflow-y-auto">
      <div class="lead-gen-page">
        <!-- Header -->
        <div class="lead-gen-header">
          <div class="lead-gen-header-left">
            <h1 class="lead-gen-title">Lead Generation</h1>
            <p class="lead-gen-subtitle">
              Capture website enquiries using embedded forms and popups. NEXCEO
              will create leads, qualify them with AI, assign projects, notify
              staff, and update reports automatically.
            </p>
          </div>
          <div class="lead-gen-badges">
            <span
              class="badge"
              :class="contactForm?.enabled ? 'badge-green' : 'badge-gray'"
            >
              Contact Form {{ contactForm?.enabled ? 'Active' : 'Disabled' }}
            </span>
            <span v-if="isDirtyContact" class="badge badge-amber">
              Contact Form — Unsaved
            </span>
            <span
              class="badge"
              :class="popupForm?.enabled ? 'badge-green' : 'badge-gray'"
            >
              Popup {{ popupForm?.enabled ? 'Active' : 'Disabled' }}
            </span>
            <span v-if="isDirtyPopup" class="badge badge-amber">
              Popup — Unsaved
            </span>
            <span class="badge badge-indigo">AI Follow-up Enabled</span>
          </div>
        </div>

        <!-- Stats cards -->
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-label">Website Leads</div>
            <div class="stat-value">{{ stats.total_website_leads ?? '—' }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Contact Form Leads</div>
            <div class="stat-value">{{ stats.contact_form_leads ?? '—' }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Popup Leads</div>
            <div class="stat-value">{{ stats.popup_leads ?? '—' }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Verified Leads</div>
            <div class="stat-value">{{ stats.verified_leads ?? '—' }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Conversion Rate</div>
            <div class="stat-value">
              {{
                stats.conversion_rate != null
                  ? stats.conversion_rate + '%'
                  : '—'
              }}
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="tab-bar">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            class="tab-btn"
            :class="[activeTab === tab.key ? 'tab-btn-active' : '']"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="loading-state">Loading...</div>
        <div v-else-if="loadError" class="error-state">{{ loadError }}</div>

        <div v-else>
          <!-- OVERVIEW TAB -->
          <div v-if="activeTab === 'overview'" class="tab-panel">
            <!-- Workflow strip -->
            <div class="workflow-strip">
              <div
                v-for="(step, i) in workflowSteps"
                :key="step"
                class="workflow-step-wrap"
              >
                <div class="workflow-step">{{ step }}</div>
                <div v-if="i < workflowSteps.length - 1" class="workflow-arrow">
                  →
                </div>
              </div>
            </div>

            <!-- Recent stats -->
            <div class="overview-grid">
              <div class="overview-card">
                <div class="overview-card-title">Last 7 Days Submissions</div>
                <div class="overview-card-value">
                  {{ stats.last_7_days ?? '—' }}
                </div>
              </div>
              <div class="overview-card">
                <div class="overview-card-title">Active Forms</div>
                <div class="overview-card-value">
                  {{ stats.forms_enabled ?? '—' }}
                </div>
              </div>
            </div>

            <!-- Setup checklist -->
            <div class="checklist-panel">
              <h3 class="checklist-title">Setup Checklist</h3>
              <div class="checklist-items">
                <div
                  v-for="item in setupChecklist"
                  :key="item.label"
                  class="checklist-item"
                >
                  <span :class="item.done ? 'check-done' : 'check-pending'">
                    {{ item.done ? '✓' : '○' }}
                  </span>
                  <span :class="item.done ? '' : 'text-gray'">{{
                    item.label
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- CONTACT FORM TAB -->
          <div v-if="activeTab === 'contact'" class="tab-panel two-col">
            <div class="form-config-col">
              <h2 class="col-title">Contact Form Configuration</h2>

              <label class="toggle-row">
                <span class="toggle-label">Enable Contact Form</span>
                <input
                  v-model="contactDraft.enabled"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>

              <div class="field-group">
                <label class="field-label">Form Title</label>
                <input
                  v-model="contactDraft.title"
                  type="text"
                  class="field-input"
                  placeholder="Get Project Details"
                />
              </div>
              <div class="field-group">
                <label class="field-label">Subtitle</label>
                <input
                  v-model="contactDraft.subtitle"
                  type="text"
                  class="field-input"
                  placeholder="Share your requirement..."
                />
              </div>
              <div class="field-group">
                <label class="field-label">Default Project</label>
                <select
                  v-model="contactDraft.default_project_id"
                  class="field-input"
                >
                  <option value="">No default — let visitor choose</option>
                  <option v-for="p in projects" :key="p.id" :value="p.id">
                    {{ p.name }}
                  </option>
                </select>
              </div>

              <label class="toggle-row">
                <span class="toggle-label">Allow project selection</span>
                <input
                  v-model="contactDraft.allow_project_selection"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>

              <div class="field-section-title">Optional Fields</div>
              <div
                v-for="field in optionalContactFields"
                :key="field.key"
                class="toggle-row"
              >
                <span class="toggle-label">Show {{ field.label }}</span>
                <input
                  v-model="contactDraft[field.key]"
                  type="checkbox"
                  class="toggle-input"
                />
              </div>

              <div class="field-group">
                <label class="field-label">Consent Text</label>
                <textarea
                  v-model="contactDraft.consent_text"
                  class="field-input"
                  rows="2"
                  placeholder="I agree to receive..."
                />
              </div>
              <div class="field-group">
                <label class="field-label">Thank-you Message</label>
                <input
                  v-model="contactDraft.thank_you_message"
                  type="text"
                  class="field-input"
                />
              </div>
              <div class="field-group">
                <label class="field-label">Button Text</label>
                <input
                  v-model="contactDraft.button_text"
                  type="text"
                  class="field-input"
                  placeholder="Submit Enquiry"
                />
              </div>
              <div class="field-group">
                <label class="field-label"
                  >Redirect URL after submit (optional)</label
                >
                <input
                  v-model="contactDraft.redirect_url"
                  type="url"
                  class="field-input"
                  placeholder="https://yourwebsite.com/thank-you"
                />
              </div>

              <div v-if="isDirtyContact" class="unsaved-banner">
                Changes not yet saved — click Save Contact Form below.
              </div>
              <div class="action-row">
                <button
                  class="btn-primary"
                  :disabled="isSavingContact"
                  @click="saveContactForm"
                >
                  {{ isSavingContact ? 'Saving...' : 'Save Contact Form' }}
                </button>
                <span v-if="contactSaved" class="save-success">Saved!</span>
              </div>
            </div>

            <!-- Preview column -->
            <div class="preview-col">
              <h2 class="col-title">Live Preview</h2>
              <p class="preview-note">
                Visual only — updates as you type. Save to publish.
              </p>
              <div class="form-preview">
                <h3 class="preview-title">
                  {{ contactDraft.title || 'Get Project Details' }}
                </h3>
                <p class="preview-subtitle">
                  {{
                    contactDraft.subtitle ||
                    'Share your requirement and our AI assistant will help you.'
                  }}
                </p>
                <div class="preview-field">
                  <label>Name *</label>
                  <div class="preview-input" />
                </div>
                <div class="preview-field">
                  <label>Mobile Number *</label>
                  <div class="preview-input" />
                </div>
                <div v-if="contactDraft.show_email" class="preview-field">
                  <label>Email</label>
                  <div class="preview-input" />
                </div>
                <div
                  v-if="contactDraft.allow_project_selection"
                  class="preview-field"
                >
                  <label>Interested Project</label>
                  <div class="preview-input preview-select">▼</div>
                </div>
                <div v-if="contactDraft.show_bhk" class="preview-field">
                  <label>BHK / Requirement</label>
                  <div class="preview-input" />
                </div>
                <div v-if="contactDraft.show_budget" class="preview-field">
                  <label>Budget Range</label>
                  <div class="preview-input" />
                </div>
                <div v-if="contactDraft.show_location" class="preview-field">
                  <label>Preferred Location</label>
                  <div class="preview-input" />
                </div>
                <div v-if="contactDraft.show_purpose" class="preview-field">
                  <label>Purpose</label>
                  <div class="preview-input preview-select">▼</div>
                </div>
                <div v-if="contactDraft.show_timeline" class="preview-field">
                  <label>Timeline</label>
                  <div class="preview-input preview-select">▼</div>
                </div>
                <div v-if="contactDraft.show_message" class="preview-field">
                  <label>Message</label>
                  <div class="preview-input preview-textarea" />
                </div>
                <div class="preview-consent">
                  <input type="checkbox" disabled />
                  <span>{{
                    contactDraft.consent_text ||
                    'I agree to receive project updates and follow-up communication.'
                  }}</span>
                </div>
                <div class="preview-btn">
                  {{ contactDraft.button_text || 'Submit Enquiry' }}
                </div>
              </div>
            </div>
          </div>

          <!-- POPUP FORM TAB -->
          <div v-if="activeTab === 'popup'" class="tab-panel two-col">
            <div class="form-config-col">
              <h2 class="col-title">Popup Form Configuration</h2>

              <label class="toggle-row">
                <span class="toggle-label">Enable Popup Form</span>
                <input
                  v-model="popupDraft.enabled"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>

              <div class="field-group">
                <label class="field-label">Popup Title</label>
                <select v-model="popupDraft.title" class="field-input">
                  <option value="">Custom...</option>
                  <option v-for="t in popupTitleOptions" :key="t" :value="t">
                    {{ t }}
                  </option>
                </select>
              </div>
              <div class="field-group">
                <label class="field-label">Custom Title</label>
                <input
                  v-model="popupDraft.title"
                  type="text"
                  class="field-input"
                  placeholder="Get Project Details on WhatsApp"
                />
              </div>
              <div class="field-group">
                <label class="field-label">Subtitle</label>
                <input
                  v-model="popupDraft.subtitle"
                  type="text"
                  class="field-input"
                />
              </div>
              <div class="field-group">
                <label class="field-label">Default Project</label>
                <select
                  v-model="popupDraft.default_project_id"
                  class="field-input"
                >
                  <option value="">No default</option>
                  <option v-for="p in projects" :key="p.id" :value="p.id">
                    {{ p.name }}
                  </option>
                </select>
              </div>

              <label class="toggle-row">
                <span class="toggle-label">Allow project selection</span>
                <input
                  v-model="popupDraft.allow_project_selection"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>
              <div class="field-group">
                <label class="field-label">Button Text</label>
                <input
                  v-model="popupDraft.button_text"
                  type="text"
                  class="field-input"
                  placeholder="Get Details"
                />
              </div>
              <div class="field-group">
                <label class="field-label">Consent Text</label>
                <textarea
                  v-model="popupDraft.consent_text"
                  class="field-input"
                  rows="2"
                />
              </div>
              <div class="field-group">
                <label class="field-label">Thank-you Message</label>
                <input
                  v-model="popupDraft.thank_you_message"
                  type="text"
                  class="field-input"
                />
              </div>

              <div class="field-section-title">Trigger Settings</div>
              <div class="field-group">
                <label class="field-label">Show after (seconds)</label>
                <select
                  v-model.number="popupDraft.trigger_config.show_after_seconds"
                  class="field-input"
                >
                  <option :value="5">5 seconds</option>
                  <option :value="10">10 seconds</option>
                  <option :value="15">15 seconds</option>
                  <option :value="30">30 seconds</option>
                </select>
              </div>
              <div class="field-group">
                <label class="field-label">Show again after (days)</label>
                <select
                  v-model.number="
                    popupDraft.trigger_config.show_again_after_days
                  "
                  class="field-input"
                >
                  <option :value="1">1 day</option>
                  <option :value="7">7 days</option>
                  <option :value="15">15 days</option>
                  <option :value="30">30 days</option>
                </select>
              </div>
              <label class="toggle-row">
                <span class="toggle-label">Exit intent trigger</span>
                <input
                  v-model="popupDraft.trigger_config.exit_intent"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>
              <label class="toggle-row">
                <span class="toggle-label">Hide on mobile</span>
                <input
                  v-model="popupDraft.trigger_config.hide_on_mobile"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>

              <div v-if="isDirtyPopup" class="unsaved-banner">
                Changes not yet saved — click Save Popup Form below.
              </div>
              <div class="action-row">
                <button
                  class="btn-primary"
                  :disabled="isSavingPopup"
                  @click="savePopupForm"
                >
                  {{ isSavingPopup ? 'Saving...' : 'Save Popup Form' }}
                </button>
                <span v-if="popupSaved" class="save-success">Saved!</span>
              </div>
            </div>

            <!-- Popup preview -->
            <div class="preview-col">
              <h2 class="col-title">Live Popup Preview</h2>
              <p class="preview-note">
                Visual only — updates as you type. Save to publish.
              </p>
              <div class="popup-preview-wrap">
                <div class="popup-preview-modal">
                  <button class="popup-close-btn" disabled>✕</button>
                  <h3 class="preview-title">
                    {{ popupDraft.title || 'Get Project Details on WhatsApp' }}
                  </h3>
                  <p class="preview-subtitle">
                    {{
                      popupDraft.subtitle ||
                      'Enter your details and ZOYA will help you.'
                    }}
                  </p>
                  <div class="preview-field">
                    <label>Name *</label>
                    <div class="preview-input" />
                  </div>
                  <div class="preview-field">
                    <label>Mobile Number *</label>
                    <div class="preview-input" />
                  </div>
                  <div
                    v-if="popupDraft.allow_project_selection"
                    class="preview-field"
                  >
                    <label>Interested Project</label>
                    <div class="preview-input preview-select">▼</div>
                  </div>
                  <div class="preview-consent">
                    <input type="checkbox" disabled />
                    <span>{{
                      popupDraft.consent_text ||
                      'I agree to receive project updates on WhatsApp.'
                    }}</span>
                  </div>
                  <div class="preview-btn">
                    {{ popupDraft.button_text || 'Get Details' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- EMBED CODE TAB -->
          <div v-if="activeTab === 'embed'" class="tab-panel">
            <div v-if="!contactForm && !popupForm" class="empty-state">
              Save a Contact Form or Popup Form first to generate embed code.
            </div>
            <div v-else>
              <!-- Contact form disabled warning -->
              <div
                v-if="contactForm && !contactForm.enabled"
                class="embed-warning-red"
              >
                ⚠ Contact Form is disabled. Enable it in the Contact Form tab
                and Save before embedding on your website.
              </div>
              <!-- Domain warning -->
              <div
                v-if="
                  (contactForm?.enabled &&
                    !contactForm.allowed_domains?.length) ||
                  (popupForm?.enabled && !popupForm.allowed_domains?.length)
                "
                class="domain-warning"
              >
                ⚠ Add at least one allowed domain in Settings before embedding
                this form on your website. Admin preview still works without a
                domain.
              </div>

              <!-- Contact Form iframe -->
              <div v-if="contactForm" class="embed-block">
                <h3 class="embed-title">Contact Form — iframe Embed</h3>
                <pre class="embed-code">{{ contactIframeCode }}</pre>
                <div class="embed-actions">
                  <button
                    class="btn-secondary"
                    @click="copyCode(contactIframeCode)"
                  >
                    Copy iframe Code
                  </button>
                  <button
                    class="btn-secondary"
                    @click="showContactPreview = true"
                  >
                    Preview Form
                  </button>
                  <a
                    v-if="contactForm.enabled"
                    :href="contactFormUrl"
                    target="_blank"
                    class="btn-secondary"
                  >
                    Open Public Form
                  </a>
                  <span
                    v-else
                    class="btn-secondary btn-disabled"
                    title="Enable and save the Contact Form before opening the public form."
                  >
                    Open Public Form
                  </span>
                </div>
              </div>

              <!-- Popup script -->
              <div v-if="popupForm" class="embed-block">
                <h3 class="embed-title">Popup Form — Script Embed</h3>
                <div
                  v-if="popupForm && !popupForm.enabled"
                  class="embed-warning-inline"
                >
                  ⚠ Popup Form is disabled. Enable it in the Popup Form tab and
                  Save before embedding on your website.
                </div>
                <pre class="embed-code">{{ popupScriptCode }}</pre>
                <div class="embed-actions">
                  <button
                    class="btn-secondary"
                    @click="copyCode(popupScriptCode)"
                  >
                    Copy Popup Script
                  </button>
                  <button
                    class="btn-secondary"
                    @click="showPopupPreview = true"
                  >
                    Preview Popup
                  </button>
                  <a
                    v-if="popupForm.enabled"
                    :href="popupFormUrl"
                    target="_blank"
                    class="btn-secondary"
                  >
                    Open Public Form
                  </a>
                  <span
                    v-else
                    class="btn-secondary btn-disabled"
                    title="Enable and save the Popup Form before opening the public form."
                  >
                    Open Public Form
                  </span>
                </div>
              </div>

              <!-- Allowed domains -->
              <div class="embed-block">
                <h3 class="embed-title">Allowed Domains</h3>
                <div class="domain-list">
                  <div
                    v-for="domain in allAllowedDomains"
                    :key="domain"
                    class="domain-chip"
                  >
                    {{ domain }}
                  </div>
                  <div v-if="!allAllowedDomains.length" class="text-gray">
                    No domains configured. Forms will accept submissions from
                    any domain (not recommended for production).
                  </div>
                </div>
                <p class="embed-note">
                  Configure allowed domains under the Settings tab.
                </p>
              </div>
            </div>
          </div>

          <!-- SUBMISSIONS TAB -->
          <div v-if="activeTab === 'submissions'" class="tab-panel">
            <!-- Filters -->
            <div class="filter-row">
              <select
                v-model="subFilter.form_type"
                class="field-input filter-select"
              >
                <option value="">All Types</option>
                <option value="contact">Contact Form</option>
                <option value="popup">Popup</option>
              </select>
              <select
                v-model="subFilter.status"
                class="field-input filter-select"
              >
                <option value="">All Status</option>
                <option value="converted">Converted</option>
                <option value="duplicate">Duplicate</option>
                <option value="rejected">Rejected</option>
                <option value="spam">Spam</option>
              </select>
              <button class="btn-secondary" @click="loadSubmissions">
                Refresh
              </button>
            </div>

            <div v-if="isLoadingSubs" class="loading-state">
              Loading submissions...
            </div>
            <div v-else-if="!allSubmissions.length" class="empty-state">
              No submissions yet. Enable a form and embed it on your website to
              start capturing leads.
            </div>
            <table v-else class="submissions-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Project</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Lead Status</th>
                  <th>Agent</th>
                  <th>Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sub in allSubmissions" :key="sub.id">
                  <td>{{ sub.name }}</td>
                  <td>{{ sub.phone_number }}</td>
                  <td>{{ sub.project_name || '—' }}</td>
                  <td>
                    <span
                      class="badge-sm"
                      :class="[
                        sub.form_type === 'popup'
                          ? 'badge-blue'
                          : 'badge-green',
                      ]"
                    >
                      {{ sub.form_type === 'popup' ? 'Popup' : 'Contact' }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="badge-sm"
                      :class="[statusBadgeClass(sub.status)]"
                    >
                      {{ sub.status }}
                    </span>
                  </td>
                  <td>{{ sub.lead_stage || '—' }}</td>
                  <td>{{ sub.assigned_agent || '—' }}</td>
                  <td>{{ formatDate(sub.created_at) }}</td>
                  <td>
                    <router-link
                      v-if="sub.lead_id"
                      :to="{
                        name: 'nexceo_leads_edit',
                        params: { id: sub.lead_id },
                      }"
                      class="link-action"
                    >
                      View Lead
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- SETTINGS TAB -->
          <div v-if="activeTab === 'settings'" class="tab-panel">
            <h2 class="col-title">Lead Generation Settings</h2>

            <div class="settings-section">
              <h3 class="settings-section-title">General</h3>
              <label class="toggle-row">
                <span class="toggle-label">Enable Contact Form</span>
                <input
                  v-model="contactDraft.enabled"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>
              <label class="toggle-row">
                <span class="toggle-label">Enable Popup Form</span>
                <input
                  v-model="popupDraft.enabled"
                  type="checkbox"
                  class="toggle-input"
                />
              </label>
            </div>

            <div class="settings-section">
              <h3 class="settings-section-title">Allowed Domains</h3>
              <p class="settings-help">
                Separate with commas. Example: clientwebsite.com,
                www.clientwebsite.com
              </p>
              <input
                v-model="allowedDomainsInput"
                type="text"
                class="field-input"
                placeholder="yourwebsite.com, www.yourwebsite.com"
              />
            </div>

            <div class="settings-section">
              <h3 class="settings-section-title">Spam Protection</h3>
              <div class="settings-info">
                Honeypot field, rate limiting (5 submissions per phone per 5
                minutes), and phone validation are active by default. No
                configuration required.
              </div>
            </div>

            <div class="settings-section">
              <h3 class="settings-section-title">Duplicate Lead Handling</h3>
              <div class="settings-info">
                If the same phone number submits again, the existing lead is
                updated (not duplicated) and a new activity is logged.
              </div>
            </div>

            <div class="action-row">
              <button
                class="btn-primary"
                :disabled="isSavingSettings"
                @click="saveSettings"
              >
                {{ isSavingSettings ? 'Saving...' : 'Save Settings' }}
              </button>
              <span v-if="settingsSaved" class="save-success">Saved!</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Admin Contact Form Preview Modal -->
      <div
        v-if="showContactPreview"
        class="modal-overlay"
        @click.self="showContactPreview = false"
      >
        <div class="modal-card">
          <div class="modal-header">
            <span class="modal-title"
              >Admin Preview — Contact Form (Visual Only)</span
            >
            <button class="modal-close" @click="showContactPreview = false">
              ✕
            </button>
          </div>
          <div class="form-preview modal-preview">
            <h3 class="preview-title">
              {{ contactDraft.title || 'Get Project Details' }}
            </h3>
            <p class="preview-subtitle">
              {{
                contactDraft.subtitle ||
                'Share your requirement and our AI assistant will help you.'
              }}
            </p>
            <div class="preview-field">
              <label>Full Name *</label>
              <div class="preview-input" />
            </div>
            <div class="preview-field">
              <label>Mobile Number *</label>
              <div class="preview-input" />
            </div>
            <div v-if="contactDraft.show_email" class="preview-field">
              <label>Email</label>
              <div class="preview-input" />
            </div>
            <div
              v-if="contactDraft.allow_project_selection"
              class="preview-field"
            >
              <label>Interested Project</label>
              <div class="preview-input preview-select">
                {{
                  projects.length
                    ? '▼ Select project...'
                    : 'No active projects available'
                }}
              </div>
            </div>
            <div v-if="contactDraft.show_bhk" class="preview-field">
              <label>BHK / Requirement</label>
              <div class="preview-input preview-select">▼</div>
            </div>
            <div v-if="contactDraft.show_budget" class="preview-field">
              <label>Budget Range</label>
              <div class="preview-input" />
            </div>
            <div v-if="contactDraft.show_location" class="preview-field">
              <label>Preferred Location</label>
              <div class="preview-input" />
            </div>
            <div v-if="contactDraft.show_purpose" class="preview-field">
              <label>Purpose</label>
              <div class="preview-input preview-select">▼</div>
            </div>
            <div v-if="contactDraft.show_timeline" class="preview-field">
              <label>Timeline</label>
              <div class="preview-input preview-select">▼</div>
            </div>
            <div v-if="contactDraft.show_message" class="preview-field">
              <label>Message</label>
              <div class="preview-input preview-textarea" />
            </div>
            <div class="preview-consent">
              <input type="checkbox" disabled />
              <span>{{
                contactDraft.consent_text ||
                'I agree to receive project updates and follow-up communication.'
              }}</span>
            </div>
            <div class="preview-btn">
              {{ contactDraft.button_text || 'Submit Enquiry' }}
            </div>
            <p class="preview-powered-by">Powered by NEXCEO CRM</p>
          </div>
        </div>
      </div>

      <!-- Admin Popup Preview Modal -->
      <div
        v-if="showPopupPreview"
        class="modal-overlay"
        @click.self="showPopupPreview = false"
      >
        <div class="modal-card modal-card-sm">
          <div class="modal-header">
            <span class="modal-title"
              >Admin Preview — Popup Form (Visual Only)</span
            >
            <button class="modal-close" @click="showPopupPreview = false">
              ✕
            </button>
          </div>
          <div class="popup-preview-modal popup-preview-modal--flat">
            <h3 class="preview-title">
              {{ popupDraft.title || 'Get Project Details on WhatsApp' }}
            </h3>
            <p class="preview-subtitle">
              {{
                popupDraft.subtitle ||
                'Enter your details and ZOYA will help you.'
              }}
            </p>
            <div class="preview-field">
              <label>Name *</label>
              <div class="preview-input" />
            </div>
            <div class="preview-field">
              <label>Mobile Number *</label>
              <div class="preview-input" />
            </div>
            <div
              v-if="popupDraft.allow_project_selection"
              class="preview-field"
            >
              <label>Interested Project</label>
              <div class="preview-input preview-select">
                {{
                  projects.length
                    ? '▼ Select project...'
                    : 'No active projects available'
                }}
              </div>
            </div>
            <div class="preview-consent">
              <input type="checkbox" disabled />
              <span>{{
                popupDraft.consent_text ||
                'I agree to receive project updates on WhatsApp.'
              }}</span>
            </div>
            <div class="preview-btn">
              {{ popupDraft.button_text || 'Get Details' }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- close overflow-y-auto -->
  </div>
  <!-- close h-full -->
</template>

<style scoped>
.lead-gen-page {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  padding-bottom: 64px;
}

.lead-gen-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.lead-gen-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.lead-gen-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  max-width: 580px;
}

.lead-gen-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: flex-start;
}

.badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-green {
  background: #d1fae5;
  color: #065f46;
}

.badge-gray {
  background: #f3f4f6;
  color: #6b7280;
}

.badge-indigo {
  background: #e0e7ff;
  color: #3730a3;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
}

.stat-label {
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 6px;
  font-weight: 500;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}

.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 24px;
}

.tab-btn {
  padding: 10px 18px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
}

.tab-btn:hover {
  color: #374151;
}

.tab-btn-active {
  color: #6366f1;
  border-bottom-color: #6366f1;
}

.tab-panel {
  padding-top: 4px;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.form-config-col,
.preview-col {
  min-width: 0;
}

.col-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px;
}

.field-group {
  margin-bottom: 16px;
}

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff;
  color: #111827;
}

.field-input:focus {
  outline: none;
  border-color: #6366f1;
}

.field-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin: 20px 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}

.toggle-label {
  font-size: 14px;
  color: #374151;
}

.toggle-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
}

.btn-primary {
  padding: 10px 22px;
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn-primary:disabled {
  background: #a5b4fc;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 9px 18px;
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.save-success {
  font-size: 13px;
  color: #059669;
  font-weight: 500;
}

/* Form preview */
.form-preview {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.preview-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.preview-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 18px;
}

.preview-field {
  margin-bottom: 12px;
}

.preview-field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.preview-input {
  height: 36px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 7px;
}

.preview-select {
  display: flex;
  align-items: center;
  padding: 0 12px;
  color: #9ca3af;
  font-size: 13px;
}

.preview-textarea {
  height: 60px;
}

.preview-consent {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 14px;
}

.preview-btn {
  width: 100%;
  padding: 11px;
  background: #6366f1;
  color: #fff;
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
}

.preview-powered-by {
  text-align: center;
  font-size: 11px;
  color: #d1d5db;
  margin-top: 12px;
}

/* Popup preview */
.popup-preview-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 32px;
  min-height: 300px;
}

.popup-preview-modal {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  max-width: 360px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.popup-preview-modal--flat {
  box-shadow: none;
  padding: 20px;
}

.popup-close-btn {
  position: absolute;
  top: 10px;
  right: 14px;
  background: none;
  border: none;
  font-size: 18px;
  color: #9ca3af;
  cursor: default;
}

/* Workflow strip */
.workflow-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  background: #f0f4ff;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.workflow-step-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.workflow-step {
  background: #6366f1;
  color: #fff;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.workflow-arrow {
  color: #818cf8;
  font-size: 16px;
}

/* Overview */
.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.overview-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
}

.overview-card-title {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 8px;
}

.overview-card-value {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
}

/* Checklist */
.checklist-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
}

.checklist-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #374151;
}

.check-done {
  color: #059669;
  font-weight: 700;
  font-size: 16px;
}

.check-pending {
  color: #d1d5db;
  font-size: 16px;
}

.text-gray {
  color: #9ca3af;
}

/* Embed */
.embed-block {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.embed-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 14px;
}

.embed-code {
  background: #0f172a;
  color: #e2e8f0;
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  font-family: monospace;
  overflow-x: auto;
  margin-bottom: 12px;
  white-space: pre;
}

.embed-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.embed-note {
  font-size: 12px;
  color: #9ca3af;
  margin: 8px 0 0;
}

.domain-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.domain-chip {
  background: #f0f4ff;
  color: #3730a3;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.domain-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 20px;
}

/* Submissions */
.filter-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-select {
  width: 160px;
}

.submissions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.submissions-table th {
  background: #f9fafb;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.submissions-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.badge-sm {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.badge-blue {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-green {
  background: #d1fae5;
  color: #065f46;
}

.badge-yellow {
  background: #fef3c7;
  color: #92400e;
}

.badge-red {
  background: #fee2e2;
  color: #991b1b;
}

.link-action {
  color: #6366f1;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
}

/* Settings */
.settings-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
}

.settings-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 14px;
}

.settings-help {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 12px;
}

.settings-info {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
}

/* States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #9ca3af;
  font-size: 15px;
}

.error-state {
  color: #dc2626;
  background: #fee2e2;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

/* Unsaved / dirty state */
.badge-amber {
  background: #fef3c7;
  color: #92400e;
}

.unsaved-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 12px;
}

/* Preview note */
.preview-note {
  font-size: 12px;
  color: #9ca3af;
  margin: -14px 0 16px;
  font-style: italic;
}

/* Embed warnings */
.embed-warning-red {
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  color: #991b1b;
  margin-bottom: 16px;
}

.embed-warning-inline {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 12px;
}

/* Disabled button state */
.btn-disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

/* Admin preview modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-card {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.modal-card-sm {
  max-width: 420px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.modal-close {
  background: none;
  border: none;
  font-size: 18px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
  padding: 2px 6px;
}

.modal-close:hover {
  color: #374151;
}

.modal-preview {
  margin: 0;
  border: none;
  border-radius: 0 0 16px 16px;
}
</style>
